import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Warning,
  Link as LinkIcon,
  TextT,
  CircleNotch,
  PlayCircle,
  Copy,
  Check,
  EnvelopeSimple,
  Lifebuoy,
  ShoppingBag,
  ClockCounterClockwise,
  Trash,
  CaretDown,
  CaretRight,
} from "@phosphor-icons/react";
import { t } from "../i18n";
import {
  rejectionHelper,
  rejectionHistory,
  getRejectionReport,
  deleteRejectionReport,
} from "../lib/api";
import ApplyToShopifyModal from "./ApplyToShopifyModal";
import ApplyAllToShopifyModal from "./ApplyAllToShopifyModal";
import { FilePdf, Cloud } from "@phosphor-icons/react";

const REJECTION_PDF_URL = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/rejection_helper/${id}/export.pdf`;
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const LANG_CODES = ["en", "fr", "es", "it", "pt", "de"];

function CopyButton({ text, label = "Copy", lang }) {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 border border-neutral-900 bg-white hover:bg-neutral-900 hover:text-white px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest ma-trans"
    >
      {copied ? <Check size={12} weight="bold" /> : <Copy size={12} />}
      {copied ? t(lang, "copied") : label}
    </button>
  );
}

function ConfidenceBadge({ level }) {
  const styles = {
    high: "bg-red-50 text-red-800 border-red-400",
    medium: "bg-amber-50 text-amber-800 border-amber-400",
    low: "bg-neutral-50 text-neutral-700 border-neutral-400",
  };
  return (
    <span className={`text-[9px] font-mono uppercase tracking-widest border px-1.5 py-0.5 ${styles[level] || styles.medium}`}>
      {level}
    </span>
  );
}

export default function RejectionPanel({ lang }) {
  const [rejectionEmail, setRejectionEmail] = useState("");
  const [mode, setMode] = useState("url"); // url | text
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [productType, setProductType] = useState("");
  const [outputLang, setOutputLang] = useState(lang);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [applyTarget, setApplyTarget] = useState(null); // {original, suggested}
  const [applyAllOpen, setApplyAllOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);

  React.useEffect(() => setOutputLang(lang), [lang]);

  const refreshHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const list = await rejectionHistory(30);
      setHistory(list || []);
    } catch {
      /* silent */
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => { refreshHistory(); }, [refreshHistory]);

  const loadHistoryItem = async (id) => {
    try {
      const r = await getRejectionReport(id);
      setReport(r);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const deleteHistoryItem = async (id) => {
    if (!window.confirm(t(lang, "rejection_history_confirm_delete"))) return;
    try {
      await deleteRejectionReport(id);
      setHistory((prev) => prev.filter((x) => x.id !== id));
      if (report && report.id === id) setReport(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!rejectionEmail.trim()) {
      toast.error(t(lang, "rejection_email_required"));
      return;
    }
    if (mode === "url" && !url.trim()) return;
    if (mode === "text" && !text.trim()) return;

    setLoading(true);
    setReport(null);
    try {
      const res = await rejectionHelper({
        rejection_reason: rejectionEmail.trim(),
        mode,
        url: mode === "url" ? url.trim() : undefined,
        text: mode === "text" ? text : undefined,
        language: outputLang,
        product_type: productType.trim() || undefined,
      });
      setReport(res);
      refreshHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="rejection-panel">
      {/* Rejection history */}
      <div className="border border-neutral-900 bg-white" data-testid="rejection-history-panel">
        <button
          type="button"
          onClick={() => setHistoryOpen(!historyOpen)}
          className="w-full flex items-center gap-2 px-4 py-3 border-b border-neutral-900 bg-neutral-900 text-white text-left ma-trans hover:bg-neutral-800"
          data-testid="rejection-history-toggle"
        >
          {historyOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
          <ClockCounterClockwise size={14} weight="fill" />
          <div className="text-xs uppercase tracking-[0.2em] font-mono">
            {t(lang, "rejection_history")}
          </div>
          <div className="ml-auto text-[10px] font-mono opacity-70" data-testid="rejection-history-count">
            {history.length}
          </div>
        </button>
        {historyOpen && (
          <div className="max-h-72 overflow-auto" data-testid="rejection-history-list">
            {historyLoading && history.length === 0 ? (
              <div className="px-4 py-6 text-[11px] font-mono text-neutral-500 text-center">…</div>
            ) : history.length === 0 ? (
              <div className="px-4 py-6 text-[11px] font-mono text-neutral-500 text-center" data-testid="rejection-history-empty">
                {t(lang, "rejection_history_empty")}
              </div>
            ) : (
              history.map((h) => (
                <div
                  key={h.id}
                  className="flex items-center gap-3 px-4 py-2.5 border-t border-neutral-100 hover:bg-neutral-50 ma-trans"
                  data-testid={`rejection-history-item-${h.id}`}
                >
                  <button
                    type="button"
                    onClick={() => loadHistoryItem(h.id)}
                    className="flex-1 text-left min-w-0"
                    data-testid={`rejection-history-load-${h.id}`}
                  >
                    <div className="text-sm text-neutral-900 truncate">
                      {h.source || "—"}
                    </div>
                    <div className="text-[10px] font-mono text-neutral-500 truncate">
                      {new Date(h.created_at).toLocaleString()} · {h.rewrites_count} {t(lang, "rejection_history_rewrites")} · {h.language}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteHistoryItem(h.id)}
                    className="text-neutral-400 hover:text-red-600 ma-trans p-1"
                    title={t(lang, "rejection_history_delete")}
                    aria-label="delete"
                    data-testid={`rejection-history-delete-${h.id}`}
                  >
                    <Trash size={14} />
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <form
        onSubmit={submit}
        className="border border-neutral-900 bg-white"
        data-testid="rejection-form"
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-neutral-900 bg-neutral-900 text-white">
          <Lifebuoy size={14} weight="fill" />
          <div className="text-xs uppercase tracking-[0.2em] font-mono">
            {t(lang, "rejection_title")}
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Rejection email */}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 mb-2">
              {t(lang, "rejection_email_label")}
            </label>
            <textarea
              value={rejectionEmail}
              onChange={(e) => setRejectionEmail(e.target.value)}
              placeholder={t(lang, "rejection_email_ph")}
              rows={5}
              className="w-full border border-neutral-900 bg-white px-3 py-2.5 font-mono text-xs text-neutral-900 placeholder:text-neutral-400 ma-focus resize-y"
              data-testid="rejection-email-input"
              required
            />
          </div>

          {/* Mode tabs */}
          <div className="flex border border-neutral-900 w-fit">
            <button
              type="button"
              onClick={() => setMode("url")}
              className={`flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-widest font-mono ma-trans ${
                mode === "url"
                  ? "bg-neutral-900 text-white"
                  : "bg-white text-neutral-900 hover:bg-neutral-100"
              }`}
              data-testid="rejection-mode-url"
            >
              <LinkIcon size={12} />
              {t(lang, "input_mode_url")}
            </button>
            <button
              type="button"
              onClick={() => setMode("text")}
              className={`flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-widest font-mono border-l border-neutral-900 ma-trans ${
                mode === "text"
                  ? "bg-neutral-900 text-white"
                  : "bg-white text-neutral-900 hover:bg-neutral-100"
              }`}
              data-testid="rejection-mode-text"
            >
              <TextT size={12} />
              {t(lang, "input_mode_text")}
            </button>
          </div>

          {mode === "url" ? (
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 mb-2">
                {t(lang, "rejection_product_url")}
              </label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={t(lang, "url_placeholder")}
                className="w-full border border-neutral-900 bg-white px-3 py-2.5 font-mono text-sm ma-focus"
                data-testid="rejection-url-input"
                required
              />
            </div>
          ) : (
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 mb-2">
                {t(lang, "rejection_product_text")}
              </label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={t(lang, "rejection_product_text_ph")}
                rows={6}
                className="w-full border border-neutral-900 bg-white px-3 py-2.5 font-mono text-sm ma-focus resize-y"
                data-testid="rejection-text-input"
              />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 mb-2">
                {t(lang, "rejection_product_type")}
              </label>
              <input
                type="text"
                value={productType}
                onChange={(e) => setProductType(e.target.value)}
                placeholder={t(lang, "rejection_product_type_ph")}
                className="w-full border border-neutral-900 bg-white px-3 py-2.5 font-mono text-sm ma-focus"
                data-testid="rejection-product-type"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 mb-2">
                {t(lang, "output_language")}
              </label>
              <Select value={outputLang} onValueChange={setOutputLang}>
                <SelectTrigger
                  className="h-10 w-full rounded-none border border-neutral-900 font-mono shadow-none"
                  data-testid="rejection-output-language"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none border border-neutral-900 shadow-none">
                  {LANG_CODES.map((c) => (
                    <SelectItem key={c} value={c} className="rounded-none font-mono">
                      {c.toUpperCase()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-60 px-4 py-3 text-sm font-medium uppercase tracking-widest ma-trans"
            data-testid="rejection-submit"
          >
            {loading ? (
              <>
                <CircleNotch size={16} className="animate-spin" />
                {t(lang, "rejection_running")}
              </>
            ) : (
              <>
                <PlayCircle size={16} weight="fill" />
                {t(lang, "rejection_run")}
              </>
            )}
          </button>
        </div>
      </form>

      {/* Report */}
      {report && (
        <div className="space-y-6 ma-in" data-testid="rejection-report">
          <div className="border border-amber-500 bg-amber-50 p-5">
            <div className="flex items-center gap-2 mb-2">
              <Warning size={16} className="text-amber-700" weight="fill" />
              <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-amber-800">
                {report.rejection_category}
              </div>
            </div>
            <p className="text-sm text-amber-900 leading-relaxed" data-testid="rejection-summary">
              {report.summary}
            </p>
          </div>

          {report.rewrites && report.rewrites.length > 0 ? (
            <div className="border border-neutral-900 bg-white" data-testid="rejection-rewrites">
              <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-900 bg-neutral-900 text-white">
                <div className="text-xs uppercase tracking-[0.2em] font-mono">
                  {t(lang, "rejection_rewrites")}
                </div>
                <div className="flex items-center gap-2 ml-auto">
                  <a
                    href={REJECTION_PDF_URL(report.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 border border-white/30 bg-white/0 text-white hover:bg-white/10 px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest ma-trans"
                    data-testid="rejection-export-pdf"
                  >
                    <FilePdf size={12} weight="fill" />
                    {t(lang, "rejection_export_pdf")}
                  </a>
                  <button
                    type="button"
                    onClick={() => setApplyAllOpen(true)}
                    className="inline-flex items-center gap-1.5 border border-emerald-400 bg-emerald-600 text-white hover:bg-emerald-700 px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest ma-trans"
                    data-testid="rejection-apply-all-shopify"
                  >
                    <Cloud size={12} weight="fill" />
                    {t(lang, "apply_all_btn")}
                  </button>
                  <span className="text-[10px] font-mono opacity-70">{report.rewrites.length}</span>
                </div>
              </div>
              <div>
                {report.rewrites.map((r, i) => (
                  <div
                    key={r.id}
                    className="border-t border-neutral-200 px-4 py-4"
                    data-testid={`rejection-rewrite-${i}`}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <ConfidenceBadge level={r.confidence} />
                      <span className="text-xs font-mono uppercase tracking-widest text-neutral-700">
                        {r.rule_violated}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="border border-red-300 bg-red-50">
                        <div className="px-2 py-1 border-b border-red-300 text-[10px] font-mono uppercase tracking-widest text-red-800">
                          {t(lang, "original")}
                        </div>
                        <pre className="p-3 text-xs font-mono whitespace-pre-wrap text-red-900">
                          {r.original_phrase}
                        </pre>
                      </div>
                      <div className="border border-emerald-400 bg-emerald-50">
                        <div className="flex items-center justify-between gap-2 px-2 py-1 border-b border-emerald-400">
                          <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-800">
                            {t(lang, "suggested")}
                          </span>
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => setApplyTarget({
                                original: r.original_phrase,
                                suggested: r.suggested_phrase,
                              })}
                              className="inline-flex items-center gap-1.5 border border-emerald-700 bg-emerald-700 text-white hover:bg-emerald-800 px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest ma-trans"
                              data-testid={`rejection-apply-shopify-${i}`}
                              title={t(lang, "apply_to_shopify_btn")}
                            >
                              <ShoppingBag size={12} weight="fill" />
                              {t(lang, "apply_to_shopify_btn")}
                            </button>
                            <CopyButton text={r.suggested_phrase} label={t(lang, "copy")} lang={lang} />
                          </div>
                        </div>
                        <pre className="p-3 text-xs font-mono whitespace-pre-wrap text-emerald-900">
                          {r.suggested_phrase}
                        </pre>
                      </div>
                    </div>
                    {r.explanation && (
                      <p className="mt-2 text-xs text-neutral-600 leading-relaxed">
                        {r.explanation}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="border border-neutral-300 bg-neutral-50 p-5 text-sm text-neutral-700">
              {t(lang, "rejection_no_issues")}
            </div>
          )}

          {/* Google policy checklist - 8 subcategories */}
          <div className="border border-neutral-900 bg-white" data-testid="rejection-checklist">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-neutral-900 bg-neutral-900 text-white">
              <div className="text-xs uppercase tracking-[0.2em] font-mono">
                {t(lang, "rejection_checklist")}
              </div>
            </div>
            <div className="p-4 text-xs leading-relaxed text-neutral-700">
              <p className="mb-2 text-neutral-500 font-mono uppercase tracking-widest text-[10px]">
                {t(lang, "rejection_checklist_intro")}
              </p>
              <ol className="list-decimal ml-5 space-y-1.5">
                <li>{t(lang, "rejection_cat_1")}</li>
                <li>{t(lang, "rejection_cat_2")}</li>
                <li>{t(lang, "rejection_cat_3")}</li>
                <li>{t(lang, "rejection_cat_4")}</li>
                <li>{t(lang, "rejection_cat_5")}</li>
                <li>{t(lang, "rejection_cat_6")}</li>
                <li>{t(lang, "rejection_cat_7")}</li>
                <li>{t(lang, "rejection_cat_8")}</li>
              </ol>
              <a
                href="https://support.google.com/merchants/answer/6150127"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-3 text-[10px] font-mono uppercase tracking-widest text-emerald-700 hover:text-emerald-900 underline"
              >
                {t(lang, "rejection_policy_link")}
              </a>
            </div>
          </div>

          {/* Appeal email */}
          {report.appeal_email && (
            <div className="border border-neutral-900 bg-white" data-testid="rejection-appeal">
              <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-900 bg-emerald-700 text-white">
                <div className="flex items-center gap-2">
                  <EnvelopeSimple size={14} weight="fill" />
                  <div className="text-xs uppercase tracking-[0.2em] font-mono">
                    {t(lang, "rejection_appeal_title")}
                  </div>
                </div>
                <CopyButton text={report.appeal_email} label={t(lang, "copy")} lang={lang} />
              </div>
              <pre
                className="p-5 text-sm font-mono whitespace-pre-wrap text-neutral-800 leading-relaxed"
                data-testid="rejection-appeal-text"
              >
                {report.appeal_email}
              </pre>
            </div>
          )}
        </div>
      )}

      <ApplyToShopifyModal
        open={!!applyTarget}
        onClose={() => setApplyTarget(null)}
        lang={lang}
        originalPhrase={applyTarget?.original || ""}
        suggestedPhrase={applyTarget?.suggested || ""}
      />
      <ApplyAllToShopifyModal
        open={applyAllOpen}
        onClose={() => setApplyAllOpen(false)}
        lang={lang}
        rewrites={(report?.rewrites || []).map((r) => ({
          original_phrase: r.original_phrase,
          suggested_phrase: r.suggested_phrase,
        }))}
      />
    </div>
  );
}
